// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The ledger: the triage view and the cleaning control, which are the same thing.
 *
 * Forty zero-width spaces are ONE row, not forty. Grouping by code point is what makes a real file
 * triageable instead of a wall, and keeping the policy keyed the same way the rows are displayed is
 * what guarantees that what you see is what gets applied.
 *
 * BUT THE GROUP IS (CODE POINT, CONFIDENCE), NOT CODE POINT ALONE — and that is not a refinement.
 * A perfectly ordinary README contains a zero-width joiner inside an emoji, which is legitimate, and
 * may also contain one inside an identifier, which is not. Both are U+200D. A single row reading
 * "U+200D — Remove" would promise to remove the emoji's joiner too; charsight would correctly leave
 * it alone, and the control would have lied. Two rows say the true thing:
 *
 *     U+200D ZERO WIDTH JOINER · 3 in a Latin word          [Remove]
 *     U+200D ZERO WIDTH JOINER · 37 inside emoji sequences  [Kept — legitimate use]
 *
 * TIER IS RENDERED AS WORDS, FIRST. Conveying "this is a smuggled payload" versus "this came from
 * your keyboard" by chip colour alone fails WCAG 2.1 SC 1.4.1, and it fails ordinary users too:
 * every tier below the top would look the same.
 */

import { CATEGORY_META } from '../scan/catalogue';
import { TIER_LABELS } from '../scan/types';
import type { Action, Finding, Severity, Tier } from '../scan/types';
import { blockOf, charName, formatCodePoint, scriptDisplayName, scriptOf } from '../data/unicode';
import { chipLabel } from '../core/visible';

export interface LedgerGroup {
  key: string;
  cp: number;
  category: Finding['category'];
  severity: Severity;
  tier: Tier;
  label: string;
  name: string;
  block: string;
  script: string;
  occurrences: Finding[];
  files: string[];
  /** The action the current policy will take. `expected` groups are always 'keep'. */
  action: Action;
  /** True when the group is a legitimate use charsight positively identified. */
  legitimate: boolean;
}

const SEVERITY_ORDER: Record<Severity, number> = {
  payload: 0,
  suspicious: 1,
  notable: 2,
  expected: 3,
};

/** A short phrase describing what makes this group of occurrences different from the others. */
function contextPhrase(f: Finding): string {
  if (f.severity === 'expected') return 'legitimate use';
  if (f.severity === 'payload') return 'decodes to a message';
  return '';
}

export function buildLedger(
  perFile: readonly { name: string; findings: readonly Finding[] }[],
  actionFor: (f: Finding) => Action,
): LedgerGroup[] {
  const groups = new Map<string, LedgerGroup>();

  for (const file of perFile) {
    for (const f of file.findings) {
      // A run finding (a tag payload, a mixed-script token) has no single code point, so it groups
      // by category instead — one row per payload is correct there, because each payload says a
      // different thing.
      const key =
        f.cp >= 0 ? `${f.cp}:${f.severity}` : `${f.category}:${f.severity}:${f.byteStart}:${file.name}`;
      let g = groups.get(key);
      if (!g) {
        const meta = CATEGORY_META[f.category];
        g = {
          key,
          cp: f.cp,
          category: f.category,
          severity: f.severity,
          tier: meta.tier,
          label: meta.label,
          name: f.cp >= 0 ? (charName(f.cp) ?? 'unnamed code point') : meta.label,
          block: f.cp >= 0 ? blockOf(f.cp) : '',
          script: f.cp >= 0 ? scriptDisplayName(scriptOf(f.cp)) : '',
          occurrences: [],
          files: [],
          action: actionFor(f),
          legitimate: f.severity === 'expected',
        };
        groups.set(key, g);
      }
      g.occurrences.push(f);
      if (!g.files.includes(file.name)) g.files.push(file.name);
    }
  }

  return [...groups.values()].sort(
    (a, b) =>
      SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
      a.tier - b.tier ||
      b.occurrences.length - a.occurrences.length ||
      a.cp - b.cp,
  );
}

/** `U+200B ZERO WIDTH SPACE`, or the category label for a run finding. */
export function groupTitle(g: LedgerGroup): string {
  return g.cp >= 0 ? `${formatCodePoint(g.cp)} ${g.name}` : g.label;
}

export function groupChip(g: LedgerGroup): string {
  return g.cp >= 0 ? chipLabel(g.cp) : '·';
}

export function groupSubtitle(g: LedgerGroup): string {
  const bits: string[] = [];
  const n = g.occurrences.length;
  const ctx = contextPhrase(g.occurrences[0]!);
  bits.push(`${n} ${n === 1 ? 'occurrence' : 'occurrences'}${ctx ? ` · ${ctx}` : ''}`);
  if (g.files.length > 1) bits.push(`across ${g.files.length} files`);
  if (g.block) bits.push(g.block);
  return bits.join(' · ');
}

export function tierWord(g: LedgerGroup): string {
  return TIER_LABELS[g.tier];
}

/**
 * Which actions may this group be set to?
 *
 * A legitimate use offers none: the control is replaced by the reason it is being kept, because a
 * disabled dropdown invites the user to fight it and a stated reason does not.
 */
export function availableActions(g: LedgerGroup): Action[] {
  if (g.legitimate) return [];
  const hasReplacement = g.occurrences.some((f) => f.replacement !== undefined);
  return hasReplacement ? ['keep', 'replace', 'remove'] : ['keep', 'remove'];
}

export const ACTION_LABELS: Record<Action, string> = {
  keep: 'Keep',
  replace: 'Replace',
  remove: 'Remove',
};
